import { useMutation } from '@apollo/client';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { VariablesOf } from '@newfront-insurance/graphql-frontend';

import { GENERATE_FILE_CONTENTS_URL } from './generate-file-contents-url';

export const FILE_PREVIEW_ANALYTICS_EVENT = 'File - preview';

export interface DownloadFileArgs {
  fileUuid: string;
  filename?: string;
}

interface UseDownloadFileOptions {
  onPreviewEmailLogger?: (fileUuid: string) => void;
}

export function useDownloadFile(options?: UseDownloadFileOptions) {
  const [generateFileContentsUrl, { loading }] = useMutation(GENERATE_FILE_CONTENTS_URL);
  const analytics = useAnalytics();

  // The central BFF Apollo client runs mutations with `errorPolicy: 'all'`, so a GraphQL error
  // resolves the promise with `{ data: null, errors }` rather than rejecting. The legacy tRPC path
  // this hook replaced rejected on error, and consumers rely on that (their own `catch` blocks show
  // download/preview error toasts). Re-throw here to preserve that contract, and suppress the
  // generic auto-notification so those callers remain the single source of error UI.
  const requestContentsUrl = async (input: VariablesOf<typeof GENERATE_FILE_CONTENTS_URL>['input']) => {
    const { data, errors } = await generateFileContentsUrl({
      variables: { input },
      context: { notifications: { preventErrorNotification: true } },
    });
    if (errors?.length) {
      throw new Error(errors[0]?.message ?? 'Unable to generate file contents URL');
    }
    return data?.generateFileContentsUrl;
  };

  return {
    /**
     * Downloads file by the given file uuid
     *
     * @param fileUuid the file uuid
     * @param filename an optional filename, defaults to the original filename
     */
    download: async (fileUuid: string, filename?: string) => {
      void analytics.track({
        event: 'File - download',
        properties: {
          fileUuid,
        },
      });

      const result = await requestContentsUrl({ fileId: fileUuid, fileName: filename, downloadType: 'attachment' });
      const downloadUrl = result?.downloadUrl;

      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;

        document.body.appendChild(link);

        link.click();
      }
    },
    /**
     * Previews file in a new tab, non-previewable file types will be downloaded
     *
     * @param fileUuid the file uuid
     * @param filename an optional filename, defaults to the original filename
     */
    preview: async (fileUuid: string, filename?: string) => {
      void analytics.track({
        event: FILE_PREVIEW_ANALYTICS_EVENT,
        properties: {
          fileUuid,
        },
      });

      const result = await requestContentsUrl({ fileId: fileUuid, fileName: filename, downloadType: 'inline' });
      const downloadUrl = result?.downloadUrl;
      const isEmailLogger = result?.isEmailLogger;

      if (isEmailLogger && options?.onPreviewEmailLogger) {
        options.onPreviewEmailLogger(fileUuid);

        return;
      }

      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
      return downloadUrl;
    },
    /**
     *
     * @param fileUuid
     * @returns The previewUrl of the file
     */
    previewUrl: async (fileUuid: string) => {
      const result = await requestContentsUrl({ fileId: fileUuid, downloadType: 'inline' });
      // Coerce to `string | null` to preserve the hook's original return contract (the legacy tRPC
      // path returned the S3 redirect `location` header, which was `string | null`). Consumers such
      // as client-dash's request-details-page assign this straight into `useState<string | null>`.
      return result?.downloadUrl ?? null;
    },

    isDownloading: loading,
  };
}
